'use client';

/**
 * CommandPaletteChat Component
 *
 * Embedded AI chat within the Command Palette.
 * Supports streaming responses and entity context.
 *
 * @module components/CommandPalette/CommandPaletteChat
 * @see Issue #236 - Cross-Entity Global Search
 */

import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { Easing } from 'framer-motion';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { iconSizes } from '../../theme/typography';
import { GlassTooltip } from '../GlassTooltip';
import { BotIcon } from '../Icons';
import type { CommandPaletteChatProps, PaletteChatMessage } from './types';

// ============================================================================
// Styled Components
// ============================================================================

const ChatContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
});

const ContextBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.05),
}));

const ContextPill = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.25, 1),
  borderRadius: 16,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  fontSize: '0.75rem',
  fontWeight: 500,
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

// `$`-prefixed transient props must be filtered out of the DOM forwarding
// path; emotion (MUI's styled engine) doesn't strip them automatically the
// way styled-components does, so without `shouldForwardProp` React warns
// `Invalid attribute name: \`$isUser\`` on every render.
const transientProp = (prop: string) => prop !== '$isUser';

const MessageBubble = styled(motion.div, { shouldForwardProp: transientProp })<{ $isUser: boolean }>(
  ({ theme, $isUser }) => ({
    display: 'flex',
    gap: theme.spacing(1.5),
    maxWidth: '100%',
    flexDirection: $isUser ? 'row-reverse' : 'row',
  }),
);

const Avatar = styled(Box, { shouldForwardProp: transientProp })<{ $isUser: boolean }>(({ theme, $isUser }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: $isUser
    ? theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.2)
      : alpha(theme.palette.primary.main, 0.1)
    : theme.palette.mode === 'dark'
      ? alpha(theme.palette.secondary.main, 0.2)
      : alpha(theme.palette.secondary.main, 0.1),
  color: $isUser ? theme.palette.primary.main : theme.palette.secondary.main,
}));

const MessageContent = styled(Box, { shouldForwardProp: transientProp })<{ $isUser: boolean }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  maxWidth: 'calc(100% - 44px)',
}));

const MessageText = styled(Typography, { shouldForwardProp: transientProp })<{ $isUser: boolean }>(
  ({ theme, $isUser }) => ({
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    borderTopLeftRadius: $isUser ? 12 : 4,
    borderTopRightRadius: $isUser ? 4 : 12,
    backgroundColor: $isUser
      ? theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.2)
        : alpha(theme.palette.primary.main, 0.1)
      : theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.05)
        : alpha(theme.palette.common.black, 0.04),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: '0.875rem',
    lineHeight: 1.5,
  }),
);

const StreamingIndicator = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
}));

const BlinkingCursor = styled('span')(({ theme }) => ({
  display: 'inline-block',
  width: 2,
  height: '1em',
  backgroundColor: theme.palette.primary.main,
  marginLeft: 2,
  animation: 'blink 1s infinite',
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
}));

const SourcesAccordion = styled(Accordion)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '& .MuiAccordionSummary-root': {
    minHeight: 'auto',
    padding: 0,
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
  '& .MuiAccordionDetails-root': {
    padding: theme.spacing(1, 0),
  },
}));

const SourceItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  borderRadius: 6,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.03) : alpha(theme.palette.common.black, 0.02),
  fontSize: '0.75rem',
  '&:last-child': {
    marginBottom: 0,
  },
}));

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.black, 0.02),
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1, 1.5),
  borderRadius: 8,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.05) : alpha(theme.palette.common.black, 0.04),
  fontSize: '0.875rem',
  '& .MuiInputBase-input': {
    padding: 0,
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: theme.spacing(3),
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

// ============================================================================
// Animation Variants
// ============================================================================

// Easing curve for animations
const easeOutCubic: Easing = [0.215, 0.61, 0.355, 1];

const messageVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: easeOutCubic },
  },
};

// ============================================================================
// Component
// ============================================================================

export const CommandPaletteChat: React.FC<CommandPaletteChatProps> = ({
  messages,
  isStreaming = false,
  entityContext,
  onSendMessage,
  onClearContext,
  onClearHistory,
  placeholder,
  maxVisibleMessages = 5,
}) => {
  const { t } = useI18n();
  const reducedMotion = useReducedMotion();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [inputValue, setInputValue] = useState('');

  // Display placeholder
  const displayPlaceholder = placeholder ?? t('palette.chat.placeholder');

  // Visible messages (last N)
  const visibleMessages = messages.slice(-maxVisibleMessages);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Handle send
  const handleSend = useCallback(() => {
    if (!inputValue.trim() || isStreaming) return;
    onSendMessage(inputValue.trim());
    setInputValue('');
  }, [inputValue, isStreaming, onSendMessage]);

  // Handle key down
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Render message
  const renderMessage = (message: PaletteChatMessage, _index: number) => {
    const isUser = message.role === 'user';

    return (
      <MessageBubble
        key={message.id}
        $isUser={isUser}
        variants={reducedMotion ? undefined : messageVariants}
        initial={reducedMotion ? false : 'hidden'}
        animate="visible"
      >
        <Avatar $isUser={isUser}>
          {isUser ? <PersonIcon sx={{ fontSize: iconSizes.sm }} /> : <BotIcon sx={{ fontSize: iconSizes.sm }} />}
        </Avatar>
        <MessageContent $isUser={isUser}>
          <MessageText variant="body2" $isUser={isUser}>
            {message.content}
            {message.isStreaming && <BlinkingCursor />}
          </MessageText>

          {/* Sources */}
          {message.sources && message.sources.length > 0 && (
            <SourcesAccordion>
              <AccordionSummary expandIcon={<ExpandIcon sx={{ fontSize: iconSizes.sm }} />}>
                {t('palette.chat.sourcesCount', { count: message.sources.length })}
              </AccordionSummary>
              <AccordionDetails>
                {message.sources.map((source, i) => (
                  <SourceItem key={source.id || i}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      {Math.round(source.score * 100)}% match
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: 'inherit' }}>
                      {source.content.length > 150 ? `${source.content.substring(0, 150)}...` : source.content}
                    </Typography>
                  </SourceItem>
                ))}
              </AccordionDetails>
            </SourcesAccordion>
          )}

          {/* Entity context badge */}
          {message.entityContext && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                mt: 0.5,
              }}
            >
              Re: {message.entityContext.title}
            </Typography>
          )}
        </MessageContent>
      </MessageBubble>
    );
  };

  return (
    <ChatContainer>
      {/* Context Bar */}
      {entityContext && (
        <ContextBar>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('palette.chat.contextLabel')}:
          </Typography>
          <ContextPill>
            <Typography component="span" sx={{ fontSize: 'inherit' }}>
              {entityContext.title}
            </Typography>
          </ContextPill>
          <Box sx={{ flex: 1 }} />
          <GlassTooltip title={t('palette.chat.clearContext')}>
            <IconButton size="small" onClick={onClearContext} sx={{ opacity: 0.6 }}>
              <ClearIcon sx={{ fontSize: iconSizes.sm }} />
            </IconButton>
          </GlassTooltip>
        </ContextBar>
      )}
      {/* Messages */}
      <MessagesContainer>
        {visibleMessages.length === 0 ? (
          <EmptyState>
            <BotIcon sx={{ fontSize: iconSizes['2xl'], opacity: 0.3, mb: 1 }} />
            <Typography variant="body2">{t('palette.chat.noMessages')}</Typography>
            {entityContext && (
              <Typography variant="caption" sx={{ mt: 1 }}>
                {t('palette.chat.contextHint', { entity: entityContext.title })}
              </Typography>
            )}
          </EmptyState>
        ) : (
          <>
            {visibleMessages.map((msg, i) => renderMessage(msg, i))}

            {/* Streaming indicator */}
            {isStreaming && !visibleMessages.some((m) => m.isStreaming) && (
              <MessageBubble $isUser={false} variants={messageVariants} initial="hidden" animate="visible">
                <Avatar $isUser={false}>
                  <BotIcon sx={{ fontSize: iconSizes.sm }} />
                </Avatar>
                <MessageContent $isUser={false}>
                  <StreamingIndicator>
                    <CircularProgress size={12} />
                    <Typography variant="caption">{t('palette.chat.thinking')}</Typography>
                  </StreamingIndicator>
                </MessageContent>
              </MessageBubble>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </MessagesContainer>
      {/* Input */}
      <InputContainer>
        <StyledInputBase
          inputRef={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={displayPlaceholder}
          multiline
          maxRows={3}
          disabled={isStreaming}
          fullWidth
        />

        {isStreaming ? (
          <GlassTooltip title={t('palette.chat.stopGenerating')}>
            <IconButton color="error" size="small">
              <StopIcon />
            </IconButton>
          </GlassTooltip>
        ) : (
          <GlassTooltip title={t('palette.chat.send')}>
            <IconButton color="primary" size="small" onClick={handleSend} disabled={!inputValue.trim()}>
              <SendIcon />
            </IconButton>
          </GlassTooltip>
        )}

        {messages.length > 0 && !isStreaming && (
          <GlassTooltip title={t('palette.chat.clear')}>
            <IconButton size="small" onClick={onClearHistory} sx={{ opacity: 0.6 }}>
              <DeleteIcon sx={{ fontSize: iconSizes.md }} />
            </IconButton>
          </GlassTooltip>
        )}
      </InputContainer>
    </ChatContainer>
  );
};

CommandPaletteChat.displayName = 'CommandPaletteChat';

export default CommandPaletteChat;
